import useSWR from 'swr';
import { useSearchParams } from 'next/navigation';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { useCookies } from '@packages/shared/src/providers/CookieProvider/CookieProvider';
import { getEcState } from '@packages/shared/src/utils/ecState/ecState';
import { getEcStateFromParams, getMainAudience } from '../../../utils/audiences';
import type { PromotionVariantsData } from '../types';
import { promotionBannerDataFetcher } from './fetch/promotionBannerDataFetcher';
import { previewPath } from '../../../components/Preview/consts';

export const usePromotionBannerData = (
  fallbackPromotionData: PromotionVariantsData | undefined = undefined,
  shopId?: string,
) => {
  const query = Object.fromEntries(useSearchParams()?.entries() ?? []);
  const { language, defaultLanguage } = useI18n();
  const { getCookies } = useCookies();

  /**
   * For normal users the ecState comes from the cookie.
   * However, in the preview app, we'll need to simulate an ecState via query param "audiences".
   */
  const ecState =
    query.audiences && typeof window !== 'undefined' && window.document.URL.includes(previewPath)
      ? getEcStateFromParams(query.audiences)
      : getEcState(getCookies());

  const finalLanguage = language || defaultLanguage || 'de';
  const audience = getMainAudience(ecState);
  const promotionBannerCacheKey = `promotionBannerData-${finalLanguage}-${audience}`;
  const { data, isLoading } = useSWR<PromotionVariantsData>(
    promotionBannerCacheKey,
    () =>
      promotionBannerDataFetcher({
        query,
        cookies: getCookies(),
        locale: finalLanguage,
        callOwnApi: true,
        audience,
        shopId,
      }),
    {
      fallbackData: fallbackPromotionData,
      revalidateIfStale: false,
      // INSPIRE-3409 - do not revalidate on focus to apply correct data when getting back to the shop-browser-tab
      revalidateOnFocus: false,
    },
  );
  return {
    data,
    isLoading,
    endpoint: promotionBannerCacheKey,
    hasExpiredData: [data?.active, data?.selected].some((promoData) => promoData?.isExpired),
  };
};
